/**
 * Auth Slice
 * Manages authentication state
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/auth';

const initialState = {
  authenticated: false,
  user: null,
  userAttributes: null,
  userGroups: [],
  loading: true,
  error: null,
  passwordChangeRequired: false,
  forgotPassword: false,
  forgotPasswordSubmit: false,
  pendingUser: null,
};

// Async thunks
export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      const session = await authService.getSession();
      
      if (session) {
        const attributes = await authService.getUserAttributes();
        const groups = await authService.getUserGroups();
        
        return {
          user: session.user,
          userAttributes: attributes,
          userGroups: groups,
        };
      }
      
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const result = await authService.signIn(username, password);
      
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        return {
          challengeName: 'NEW_PASSWORD_REQUIRED',
          pendingUser: result.user,
        };
      }
      
      if (result.session) {
        const attributes = await authService.getUserAttributes();
        const groups = await authService.getUserGroups();
        
        return {
          user: result.user,
          userAttributes: attributes,
          userGroups: groups,
        };
      }
      
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const completeNewPassword = createAsyncThunk(
  'auth/completeNewPassword',
  async ({ user, newPassword }, { rejectWithValue }) => {
    try {
      const result = await authService.completeNewPassword(user, newPassword);
      
      const attributes = await authService.getUserAttributes();
      const groups = await authService.getUserGroups();
      
      return {
        user: result.user,
        userAttributes: attributes,
        userGroups: groups,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async () => {
    await authService.signOut();
  }
);

export const forgotPasswordRequest = createAsyncThunk(
  'auth/forgotPasswordRequest',
  async ({ username }, { rejectWithValue }) => {
    try {
      await authService.forgotPassword(username);
      return { username };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const forgotPasswordSubmitAction = createAsyncThunk(
  'auth/forgotPasswordSubmit',
  async ({ username, code, newPassword }, { rejectWithValue }) => {
    try {
      await authService.forgotPasswordSubmit(username, code, newPassword);
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Check if user is member of group(s)
export const memberOf = (groupPattern) => async (_, getState) => {
  const { userGroups } = getState().auth;
  
  if (typeof groupPattern === 'string') {
    return userGroups.includes(groupPattern);
  }
  
  if (groupPattern instanceof RegExp) {
    return userGroups.some((group) => groupPattern.test(group));
  }
  
  return false;
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setForgotPassword: (state, action) => {
      state.forgotPassword = action.payload;
      state.forgotPasswordSubmit = false;
    },
    setForgotPasswordSubmit: (state, action) => {
      state.forgotPasswordSubmit = action.payload;
    },
    resetAuthState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Restore session
      .addCase(restoreSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.authenticated = true;
          state.user = action.payload.user;
          state.userAttributes = action.payload.userAttributes;
          state.userGroups = action.payload.userGroups;
        }
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Sign in
      .addCase(signIn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        
        if (action.payload.challengeName === 'NEW_PASSWORD_REQUIRED') {
          state.passwordChangeRequired = true;
          state.pendingUser = action.payload.pendingUser;
        } else if (action.payload.user) {
          state.authenticated = true;
          state.user = action.payload.user;
          state.userAttributes = action.payload.userAttributes;
          state.userGroups = action.payload.userGroups;
          state.passwordChangeRequired = false;
          state.pendingUser = null;
        }
      })
      .addCase(signIn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Complete new password
      .addCase(completeNewPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeNewPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.authenticated = true;
        state.user = action.payload.user;
        state.userAttributes = action.payload.userAttributes;
        state.userGroups = action.payload.userGroups;
        state.passwordChangeRequired = false;
        state.pendingUser = null;
      })
      .addCase(completeNewPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Sign out
      .addCase(signOut.fulfilled, (state) => {
        Object.assign(state, initialState);
        state.loading = false;
      })
      
      // Forgot password request
      .addCase(forgotPasswordRequest.fulfilled, (state) => {
        state.forgotPassword = false;
        state.forgotPasswordSubmit = true;
      })
      .addCase(forgotPasswordRequest.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // Forgot password submit
      .addCase(forgotPasswordSubmitAction.fulfilled, (state) => {
        state.forgotPasswordSubmit = false;
      })
      .addCase(forgotPasswordSubmitAction.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, setForgotPassword, setForgotPasswordSubmit, resetAuthState } = authSlice.actions;

export default authSlice.reducer;
